/**
 * SoundContext — app-wide mute preference.
 *
 * Provides two values:
 *   • soundOn    — boolean; true when sound effects should play.
 *   • setSoundOn — setter with the standard `(value | fn) => void` shape.
 *
 * Persistence: this Context is initialised from and mirrors to the SAME
 * localStorage key already used by the in-game mute toggle:
 *   • "chess_sound_off" === "1"  →  muted (soundOn = false)
 *   • anything else / absent     →  sound on
 * Keeping the same key means every existing user's mute preference is
 * preserved on upgrade — nothing to migrate.
 *
 * Only the STORAGE / TOGGLE for the mute preference lives here. All
 * tone-generation functions (playTone, playMoveSound, playCaptureSound,
 * playCheckSound, playPromoSound, playMateSound, playVictorySound) stay
 * where they are in Game.jsx and continue to guard their bodies on
 * `if (!soundOn) return;` — they just read `soundOn` from this Context
 * now instead of from a Game.jsx-local useState.
 */

import { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "chess_sound_off";

// Read the persisted mute flag once, at module load, BEFORE the Provider's
// first render — matches the pattern FactionContext uses so there is no
// flash-of-wrong-state on first paint.
const INITIAL_SOUND_ON = typeof window !== "undefined"
  ? window.localStorage.getItem(STORAGE_KEY) !== "1"
  : true;

const Ctx = createContext({
  soundOn: INITIAL_SOUND_ON,
  setSoundOn: () => {},
});

export function SoundProvider({ children }) {
  const [soundOn, setSoundOn] = useState(INITIAL_SOUND_ON);

  useEffect(() => {
    // Mirror to localStorage on every change. "1" == muted, "0" == on.
    // Matches the exact value convention the old in-Game.jsx effect used
    // so pre-existing mute preferences remain readable / writable by any
    // other code that touches this key.
    try {
      window.localStorage.setItem(STORAGE_KEY, soundOn ? "0" : "1");
    } catch { /* localStorage unavailable — ignore */ }
  }, [soundOn]);

  return (
    <Ctx.Provider value={{ soundOn, setSoundOn }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSound() {
  return useContext(Ctx);
}
